import { ref, reactive, nextTick } from 'vue'
import { useStore } from 'vuex'
import { computeGroupId } from '@/views/WorkPlace/components/WorkOrder/components/hooks/useGroupOrder'
import { getMsgListFrom } from '@/api/chat.js'
import moment from 'moment'
import { message as Message } from 'ant-design-vue'
import { chatGroupTabsKeyEnum } from '@/utils/globalMap.js'

export default function useChatInfoGroup() {
  const store = useStore()
  // tab选中的key
  const groupTabsSelKey = ref(null)
  //医患群id
  const groupId = ref('')
  //私聊群id
  const privateId = ref('')
  // 导诊咨询群id
  const guideConsultGroupId = ref('')
  // 医生私聊id
  const doctorDaGroupId = ref('')
  // 企微会话使用固定 key，不进入环信 groupId 消息链路
  const weComPatientChatId = ref(chatGroupTabsKeyEnum.微信私聊患者)
  //顶部选择key
  const activeKey = ref(null)
  // Tabs 配置
  const chatInfoGroupTabs = ref([])
  // 消息对象Map
  const messageMap = reactive({})
  // 新消息提醒对象
  const newMsgTipsMap = reactive({})
  // 动态存储所有 refs 的对象
  const messageboxRefs = reactive({})
  const messageListRefs = reactive({})
  // 动态设置 ref 的方法
  const setMessageboxRef = groupId => el => {
    if (el) {
      messageboxRefs[groupId] = el
    }
  }
  const setMessageListRef = groupId => el => {
    if (el) {
      messageListRefs[groupId] = el
    }
  }
  //初始化设置Tabs
  const generateConversationList = (
    newGroupId,
    newPrivateId,
    newGuideGroupId,
    newDoctorDaGroupId,
    groupType,
    doctorPatientGroupId
  ) => {
    groupId.value = groupType === 3 ? doctorPatientGroupId : newGroupId
    privateId.value = newPrivateId
    guideConsultGroupId.value = newGuideGroupId
    doctorDaGroupId.value = newDoctorDaGroupId
    const conversations = [
      {
        title: '导诊咨询',
        key: chatGroupTabsKeyEnum.导诊咨询,
        groupId: newGuideGroupId
      },
      {
        title: '问诊会话',
        key: chatGroupTabsKeyEnum.问诊会话,
        //会存在导诊和问诊群id相同的情况 ，满足则不显示问诊会话
        //groupType === 3 groupID 设置为  doctorPatientGroupId
        groupId:
          newGuideGroupId === groupId.value
            ? null
            : groupType === 3
            ? doctorPatientGroupId
            : newGroupId
      },
      {
        title: '患者私聊',
        key: chatGroupTabsKeyEnum.患者私聊,
        groupId: newPrivateId
      },
      {
        title: '医生私聊',
        key: chatGroupTabsKeyEnum.医生私聊,
        groupId: newDoctorDaGroupId
      }
    ]
    const result = conversations.filter(conversation => conversation.groupId)
    result
      .filter(conversation => !conversation.independent)
      .forEach(conversation => {
        // 初始化消息对象
        messageMap[conversation.groupId] = messageMap[conversation.groupId] || []
        // 初始化新消息提醒对象
        newMsgTipsMap[conversation.groupId] = newMsgTipsMap[conversation.groupId] || {
          visible: false,
          num: 0,
          gapHeight: 0,
          showLoading: false,
          reloadNewMsg: false,
          loadText: '向上加载',
          page: 1,
          pageSize: 10,
          isScrollBottom: false,
          isScrollTop: false
        }
      })
    chatInfoGroupTabs.value = result
  }

  // 微信 Tab 独立增删，不进入环信消息对象和新消息提醒初始化。
  const setWeComPatientChatVisible = visible => {
    const exists = chatInfoGroupTabs.value.some(
      conversation => conversation.groupId === weComPatientChatId.value
    )
    if (visible && !exists) {
      chatInfoGroupTabs.value.push({
        title: '微信私聊患者',
        key: chatGroupTabsKeyEnum.微信私聊患者,
        groupId: weComPatientChatId.value,
        independent: true
      })
    } else if (!visible && exists) {
      chatInfoGroupTabs.value = chatInfoGroupTabs.value.filter(
        conversation => conversation.groupId !== weComPatientChatId.value
      )
    }
  }
  // 查询历史消息
  const queryHistoryMessage = (groupid, page, lastMsgMid) => {
    if (!newMsgTipsMap[groupid]) return
    newMsgTipsMap[groupid].showLoading = true
    const success = msgs => {
      newMsgTipsMap[groupid].showLoading = false
      newMsgTipsMap[groupid].page = page + 1
      if (!msgs.hasNext) {
        return (newMsgTipsMap[groupid].loadText = '')
      }
    }
    store.dispatch('getHistoryMessage', {
      groupid,
      isGroup: true,
      type: 'group',
      success,
      page,
      pageSize: newMsgTipsMap[groupid].pageSize,
      lastMsgMid
    })
  }
  // 当前最新信息来自那里 (群聊，医生私聊，患者私聊)
  async function lastMsgToGroupId(groupId, doctorDaGroupId, patientGroupId, newGuideGroupId) {
    // 过滤掉没有值的id
    const ids = [newGuideGroupId, groupId, doctorDaGroupId, patientGroupId].filter(f => !!f)
    // 没有值的情况下直接返回异常
    if (ids.length === 0) return Promise.reject()
    // 比较是不是最新的一条消息
    const last = {
      timestamp: '',
      to: computeGroupId(newGuideGroupId, groupId, doctorDaGroupId, patientGroupId) //存在没有历史消息的情况，根据优先级添加默认值
    }
    const params = {
      groupIdList: ids.join()
    }
    const result = await getMsgListFrom(params)
    result.forEach((one, index) => {
      const { timestamp, to } = one
      if (index === 0) {
        last.timestamp = timestamp
        last.to = to
      } else {
        // 判断发送时间是不是在后面
        const isAfter = moment(timestamp).isAfter(last.timestamp)
        if (isAfter) {
          last.timestamp = timestamp
          last.to = to
        }
      }
    })
    return last
  }
  //加载更多 支持查询全量消息
  const loadMoreMsgs = () => {
    const activeMsg = newMsgTipsMap[activeKey.value]
    // 尝试将 pageSize 转为数字
    const pageSize = Number(activeMsg.pageSize)

    // 检查 pageSize 的有效性
    if (isNaN(pageSize) || pageSize <= 0 || !Number.isInteger(pageSize)) {
      return Message.warning('加载的消息数量必须是一个大于 0 的整数')
    }
    if (pageSize > 100) {
      return Message.warning('最多只能加载 100 条历史消息')
    }
    if (activeMsg.showLoading) {
      return // 如果正在加载，则直接返回
    }
    const lastMsgMid = messageMap[activeKey.value]?.[0]?.mid
    queryHistoryMessage(activeKey.value, 1, lastMsgMid)
    //加载更多置顶
    nextTick(() => {
      messageboxRefs[activeKey.value].scrollTop = 0
    })
  }
  return {
    groupTabsSelKey,
    groupId,
    privateId,
    guideConsultGroupId,
    doctorDaGroupId,
    weComPatientChatId,
    activeKey,
    newMsgTipsMap,
    messageMap,
    chatInfoGroupTabs,
    messageboxRefs,
    messageListRefs,
    setMessageboxRef,
    setMessageListRef,
    queryHistoryMessage,
    lastMsgToGroupId,
    generateConversationList,
    setWeComPatientChatVisible,
    loadMoreMsgs
  }
}
